import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  StarIcon,
  ChatBubbleLeftIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { SUBMISSION_STATUS } from '../../services/programsService';

const SubmissionManager = ({
  submissions,
  selectedProgram,
  onCreateSubmission,
  onUpdateSubmission,
  userRole,
}) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    file: null,
  });
  const [uploading, setUploading] = useState(false);

  const getStatusIcon = status => {
    switch (status) {
      case SUBMISSION_STATUS.APPROVED:
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case SUBMISSION_STATUS.REJECTED:
        return <XCircleIcon className="h-5 w-5 text-red-600" />;
      case SUBMISSION_STATUS.NEEDS_REVISION:
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case SUBMISSION_STATUS.APPROVED:
        return 'bg-green-100 text-green-800';
      case SUBMISSION_STATUS.REJECTED:
        return 'bg-red-100 text-red-800';
      case SUBMISSION_STATUS.NEEDS_REVISION:
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatFileSize = bytes => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = timestamp => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const handleFileChange = event => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.google-apps.document',
        'text/plain',
        'image/jpeg',
        'image/png',
      ];

      if (!allowedTypes.includes(file.type)) {
        toast.error('File type not supported. Please upload PDF, Word, or text files.');
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size too large. Please upload files smaller than 10MB.');
        return;
      }

      setUploadData(prev => ({ ...prev, file }));
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!uploadData.title.trim()) {
      toast.error('Please enter a submission title');
      return;
    }

    if (!uploadData.file) {
      toast.error('Please select a file to upload');
      return;
    }

    setUploading(true);
    try {
      await onCreateSubmission(uploadData, uploadData.file);
      setShowUploadModal(false);
      setUploadData({ title: '', description: '', file: null });
      toast.success('Submission uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload submission');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = (fileUrl, fileName) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleStatusUpdate = async (submissionId, newStatus) => {
    try {
      await onUpdateSubmission(submissionId, { status: newStatus });
      toast.success('Submission status updated successfully!');
    } catch (error) {
      toast.error('Failed to update submission status');
    }
  };

  const filteredSubmissions = selectedProgram
    ? submissions.filter(sub => sub.programId === selectedProgram.id)
    : submissions;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Submissions</h2>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
        >
          <CloudArrowUpIcon className="h-4 w-4" />
          Upload Submission
        </button>
      </div>

      <div className="space-y-4">
        {filteredSubmissions.map(submission => (
          <motion.div
            key={submission.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <DocumentTextIcon className="h-8 w-8 text-blue-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">{submission.title}</h3>
                  <p className="text-sm text-gray-600">{submission.description}</p>
                  <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
                    <span>Submitted: {formatDate(submission.submittedAt)}</span>
                    {submission.fileMetadata && (
                      <span>Size: {formatFileSize(submission.fileMetadata.fileSize)}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span
                  className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(submission.status)}`}
                >
                  {submission.status.replace('-', ' ')}
                </span>

                <div className="flex items-center gap-2">
                  {submission.fileUrl && (
                    <button
                      onClick={() =>
                        handleDownload(submission.fileUrl, submission.fileMetadata?.fileName)
                      }
                      className="p-1 text-gray-400 transition-colors hover:text-blue-600"
                      title="Download file"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    </button>
                  )}

                  <button
                    onClick={() => setSelectedSubmission(submission)}
                    className="p-1 text-gray-400 transition-colors hover:text-blue-600"
                    title="View details"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>

                  {userRole === 'admin' && (
                    <button
                      onClick={() => setSelectedSubmission(submission)}
                      className="p-1 text-gray-400 transition-colors hover:text-yellow-600"
                      title="Review submission"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {submission.feedback && (
              <div className="mt-3 rounded-lg bg-gray-50 p-3">
                <div className="mb-2 flex items-center gap-2">
                  <ChatBubbleLeftIcon className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Admin Feedback</span>
                </div>
                <p className="text-sm text-gray-600">{submission.feedback}</p>
                {submission.rating && (
                  <div className="mt-2 flex items-center gap-1">
                    <StarIcon className="h-4 w-4 text-yellow-500" />
                    <span className="text-sm text-gray-600">{submission.rating}/5</span>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {filteredSubmissions.length === 0 && (
        <div className="py-12 text-center">
          <DocumentTextIcon className="mx-auto mb-4 h-16 w-16 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">No Submissions Yet</h3>
          <p className="mb-4 text-gray-600">
            {selectedProgram
              ? `Start working on "${selectedProgram.title}" and upload your submissions here.`
              : 'Upload your first submission to get started.'}
          </p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
          >
            Upload First Submission
          </button>
        </div>
      )}

      <AnimatePresence>
        {showUploadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Upload Submission</h3>
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Submission Title *
                  </label>
                  <input
                    type="text"
                    value={uploadData.title}
                    onChange={e => setUploadData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter submission title"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    value={uploadData.description}
                    onChange={e =>
                      setUploadData(prev => ({ ...prev, description: e.target.value }))
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Describe your submission"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">File *</label>
                  <div className="rounded-lg border-2 border-dashed border-gray-300 p-4 text-center">
                    <input
                      type="file"
                      onChange={handleFileChange}
                      className="hidden"
                      id="file-upload"
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <CloudArrowUpIcon className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                      <p className="text-sm text-gray-600">
                        {uploadData.file ? uploadData.file.name : 'Click to select file'}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        PDF, Word, or text files up to 10MB
                      </p>
                    </label>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(false)}
                    className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploading}
                    className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                  >
                    {uploading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedSubmission && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Submission Details</h3>
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">{selectedSubmission.title}</h4>
                  <p className="mt-1 text-gray-600">{selectedSubmission.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <span
                      className={`ml-2 rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(selectedSubmission.status)}`}
                    >
                      {selectedSubmission.status.replace('-', ' ')}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Submitted:</span>
                    <span className="ml-2">{formatDate(selectedSubmission.submittedAt)}</span>
                  </div>
                  {selectedSubmission.fileMetadata && (
                    <>
                      <div>
                        <span className="text-gray-600">File:</span>
                        <span className="ml-2">{selectedSubmission.fileMetadata.fileName}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Size:</span>
                        <span className="ml-2">
                          {formatFileSize(selectedSubmission.fileMetadata.fileSize)}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {selectedSubmission.fileUrl && (
                  <div>
                    <button
                      onClick={() =>
                        handleDownload(
                          selectedSubmission.fileUrl,
                          selectedSubmission.fileMetadata?.fileName
                        )
                      }
                      className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      Download File
                    </button>
                  </div>
                )}

                {userRole === 'admin' && (
                  <div className="border-t pt-4">
                    <h4 className="mb-3 font-medium text-gray-900">Admin Actions</h4>
                    <div className="flex gap-2">
                      {Object.values(SUBMISSION_STATUS).map(status => (
                        <button
                          key={status}
                          onClick={() => handleStatusUpdate(selectedSubmission.id, status)}
                          className={`rounded-md px-3 py-1 text-xs transition-colors ${
                            selectedSubmission.status === status
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {status.replace('-', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SubmissionManager;
